import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { useDispatch } from "react-redux";
import { ResourceError } from "../../../components/utils/ResourceError";
import { fetchCustomer } from "../../../api/customer";
import { axiosWithToast, mapErrors } from "../../../components/Utils";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { CustomerEditForm } from "../../../components/dashboard/customers/customer-edit-form";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

export const EditCustomer = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canEditCustomer = useUserHasAccess({ role: "customers", action: "edit" });

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    customer: {},
  });

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, customer } = itemState;

  const item = customer;

  useEffect(() => {
    getCustomer();
  }, [dispatch, id]);

  const getCustomer = async () => {
    return fetchCustomer(id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            customer: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          customer: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const handleRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      customer: data,
      isLoading: false,
    }));
  };

  const handleLoading = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const handleLoadingEnd = () => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: false,
    }));
  };

  const headerActions = [
    {
      component: null,
      label: "Back",
      description: "",
      path: `/dashboard/customers/${id}`,
      icon: ArrowLeftIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceError errors={["Customer not found"]} />;
    }

    if (!canEditCustomer) {
      return (
        <div className="mx-auto max-w-3xl mt-20 grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <ResourceError
            errors={["You don't have permission to edit customers"]}
          />
        </div>
      );
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={`Edit ${customer?.customer_number} ${customer?.name}`}
          meta={[]}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
              <CustomerEditForm
                item={item}
                onRefresh={(data) => handleRefresh(data)}
                onLoading={() => handleLoading()}
                onLoadingEnd={() => handleLoadingEnd()}
              />
            </div>
          </div>
        </DashboardHeader>
      </>
    );
  };

  return <>{renderContent()}</>;
};
